(() => {
  const ROOT_SELECTOR = '[data-pm-delivery-calendar]';
  const STATUS_SELECTOR = '[data-pm-delivery-status]';
  const OPEN_CALENDAR_EVENT = 'pm:delivery-calendar:open';
  const BUSINESS_TIME_ZONE = 'America/New_York';
  const FIRST_MONTH = { year: 2026, month: 7 };
  const LAST_MONTH = { year: 2027, month: 4 };
  const SEASON_KICKOFF_DATE = '2026-09-08';

  const DELIVERY_DATE_KEYS = Object.freeze([
    '2026-09-08', '2026-09-14', '2026-09-21', '2026-09-28',
    '2026-10-05', '2026-10-19', '2026-10-26',
    '2026-11-02', '2026-11-09', '2026-11-16', '2026-11-30',
    '2026-12-07', '2026-12-14',
    '2027-01-04', '2027-01-11', '2027-01-19', '2027-01-25',
    '2027-02-01', '2027-02-08', '2027-02-22',
    '2027-03-01', '2027-03-08', '2027-03-15', '2027-03-22', '2027-03-29',
    '2027-04-12', '2027-04-19', '2027-04-26',
    '2027-05-03', '2027-05-10', '2027-05-17', '2027-05-24'
  ]);
  const DELIVERY_DATES = new Set(DELIVERY_DATE_KEYS);

  const SCHEDULED_CLOSURE_DATES = new Set([
    '2026-09-07',
    '2026-10-12', '2026-10-13', '2026-10-14', '2026-10-15', '2026-10-16',
    '2026-11-23', '2026-11-24', '2026-11-25', '2026-11-26', '2026-11-27',
    '2026-12-21', '2026-12-22', '2026-12-23', '2026-12-24', '2026-12-25',
    '2026-12-28', '2026-12-29', '2026-12-30', '2026-12-31',
    '2027-01-01', '2027-01-18',
    '2027-02-12', '2027-02-15', '2027-02-16',
    '2027-04-05', '2027-04-06', '2027-04-07', '2027-04-08', '2027-04-09',
    '2027-05-31'
  ]);

  const NO_DELIVERY_WEEKS = [
    { start: '2026-10-12', end: '2026-10-16', reason: 'Scheduled fall closure' },
    { start: '2026-11-23', end: '2026-11-27', reason: 'Thanksgiving closure' },
    { start: '2026-12-21', end: '2026-12-25', reason: 'Scheduled winter closure' },
    { start: '2026-12-28', end: '2027-01-01', reason: 'Scheduled winter closure' },
    { start: '2027-02-15', end: '2027-02-19', reason: 'Three-day service week' },
    { start: '2027-04-05', end: '2027-04-09', reason: 'Scheduled spring closure' },
    { start: '2027-05-31', end: '2027-06-04', reason: 'No scheduled hub service' }
  ];

  const pad = (value) => String(value).padStart(2, '0');
  const toKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  const fromKey = (key) => {
    const [year, month, day] = key.split('-').map(Number);
    return new Date(year, month - 1, day, 12);
  };
  const businessDate = (instant = new Date()) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: BUSINESS_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(instant);
    const valueFor = (type) => Number(parts.find((part) => part.type === type)?.value);
    return new Date(valueFor('year'), valueFor('month') - 1, valueFor('day'), 12);
  };
  const orderWindowCutoffKey = (orderDate) => {
    const cutoff = new Date(orderDate.getFullYear(), orderDate.getMonth(), orderDate.getDate(), 12);
    const daysToFriday = (5 - cutoff.getDay() + 7) % 7;
    cutoff.setDate(cutoff.getDate() + daysToFriday);
    return toKey(cutoff);
  };
  const nextDeliveryKeyFor = (orderDate = businessDate()) => {
    const cutoffKey = orderWindowCutoffKey(orderDate);
    return DELIVERY_DATE_KEYS.find((key) => key > cutoffKey) || null;
  };
  const compareMonth = (a, b) => (a.year * 12 + a.month) - (b.year * 12 + b.month);
  const isWithin = (key, range) => key >= range.start && key <= range.end;
  const rangesOverlapMonth = (range, year, month) => {
    const first = toKey(new Date(year, month, 1, 12));
    const last = toKey(new Date(year, month + 1, 0, 12));
    return range.start <= last && range.end >= first;
  };
  const isNoDeliveryDate = (key, weekday) => weekday >= 1 && weekday <= 5 && NO_DELIVERY_WEEKS.some((range) => isWithin(key, range));
  const readableDate = (key, options = { weekday: 'short', month: 'short', day: 'numeric' }) =>
    new Intl.DateTimeFormat('en-US', options).format(fromKey(key));

  class PurefectDeliveryCalendar {
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector('[data-pm-calendar-dialog]');
      this.openButton = root.querySelector('[data-pm-calendar-open]');
      this.closeButton = root.querySelector('[data-pm-calendar-close]');
      this.previousButton = root.querySelector('[data-pm-calendar-previous]');
      this.nextButton = root.querySelector('[data-pm-calendar-next]');
      this.monthLabel = root.querySelector('[data-pm-calendar-month]');
      this.caption = root.querySelector('[data-pm-calendar-caption]');
      this.grid = root.querySelector('[data-pm-calendar-grid]');
      this.deliverySummaryLabel = root.querySelector('[data-pm-calendar-delivery-label]');
      this.deliveryList = root.querySelector('[data-pm-calendar-deliveries]');
      this.skipList = root.querySelector('[data-pm-calendar-skips]');
      this.swipeArea = root.querySelector('[data-pm-calendar-swipe]');
      this.touchStartX = null;
      this.touchStartY = null;
      this.view = this.initialMonth();
      this.returnFocusTarget = this.openButton;
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleOpenRequest = this.handleOpenRequest.bind(this);
      this.render();
      this.bindEvents();
    }

    initialMonth() {
      const now = businessDate();
      const candidate = { year: now.getFullYear(), month: now.getMonth() };
      if (compareMonth(candidate, FIRST_MONTH) < 0) return { ...FIRST_MONTH };
      if (compareMonth(candidate, LAST_MONTH) > 0) return { ...LAST_MONTH };
      return candidate;
    }

    bindEvents() {
      this.openButton.addEventListener('click', () => this.open(this.openButton));
      this.closeButton.addEventListener('click', () => this.close());
      this.previousButton.addEventListener('click', () => this.move(-1));
      this.nextButton.addEventListener('click', () => this.move(1));
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('keydown', this.handleKeydown);
      document.addEventListener(OPEN_CALENDAR_EVENT, this.handleOpenRequest);
      this.swipeArea.addEventListener('touchstart', (event) => {
        this.touchStartX = event.changedTouches[0]?.clientX ?? null;
        this.touchStartY = event.changedTouches[0]?.clientY ?? null;
      }, { passive: true });
      this.swipeArea.addEventListener('touchend', (event) => {
        if (this.touchStartX === null || this.touchStartY === null) return;
        const endX = event.changedTouches[0]?.clientX ?? this.touchStartX;
        const endY = event.changedTouches[0]?.clientY ?? this.touchStartY;
        const distanceX = endX - this.touchStartX;
        const distanceY = endY - this.touchStartY;
        this.touchStartX = null;
        this.touchStartY = null;
        if (Math.abs(distanceX) < 55 || Math.abs(distanceX) <= Math.abs(distanceY)) return;
        this.move(distanceX < 0 ? 1 : -1);
      }, { passive: true });
    }

    handleOpenRequest(event) {
      const requestedDate = event.detail?.date;
      if (requestedDate) {
        const requested = fromKey(requestedDate);
        const candidate = { year: requested.getFullYear(), month: requested.getMonth() };
        if (compareMonth(candidate, FIRST_MONTH) >= 0 && compareMonth(candidate, LAST_MONTH) <= 0) {
          this.view = candidate;
          this.render();
        }
      }
      this.open(event.detail?.trigger);
    }

    open(trigger = this.openButton) {
      this.returnFocusTarget = trigger || this.openButton;
      this.openButton.setAttribute('aria-expanded', 'true');
      document.body.classList.add('pm-delivery-calendar-open');
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
      this.closeButton.focus();
    }

    close() {
      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
      document.body.classList.remove('pm-delivery-calendar-open');
      this.openButton.setAttribute('aria-expanded', 'false');
      const focusTarget = this.returnFocusTarget?.isConnected ? this.returnFocusTarget : this.openButton;
      focusTarget.focus();
    }

    handleKeydown(event) {
      if (event.key === 'ArrowLeft' && !event.target.closest('a, button')) {
        event.preventDefault();
        this.move(-1);
      }
      if (event.key === 'ArrowRight' && !event.target.closest('a, button')) {
        event.preventDefault();
        this.move(1);
      }
    }

    move(offset) {
      const next = new Date(this.view.year, this.view.month + offset, 1, 12);
      const candidate = { year: next.getFullYear(), month: next.getMonth() };
      if (compareMonth(candidate, FIRST_MONTH) < 0 || compareMonth(candidate, LAST_MONTH) > 0) return;
      this.view = candidate;
      this.render();
    }

    render() {
      const monthDate = new Date(this.view.year, this.view.month, 1, 12);
      const monthName = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(monthDate);
      this.monthLabel.textContent = monthName;
      this.caption.textContent = `${monthName} Purefect Meals pickup calendar`;
      this.previousButton.disabled = compareMonth(this.view, FIRST_MONTH) === 0;
      this.nextButton.disabled = compareMonth(this.view, LAST_MONTH) === 0;
      this.renderGrid();
      this.renderSummary();
    }

    renderGrid() {
      const { year, month } = this.view;
      const firstWeekday = new Date(year, month, 1, 12).getDay();
      const daysInMonth = new Date(year, month + 1, 0, 12).getDate();
      const todayKey = toKey(businessDate());
      const cells = [];

      for (let index = 0; index < firstWeekday; index += 1) {
        cells.push('<td class="pm-delivery-calendar__day pm-delivery-calendar__day--empty"><span class="visually-hidden">Not part of this month</span></td>');
      }

      for (let day = 1; day <= daysInMonth; day += 1) {
        const date = new Date(year, month, day, 12);
        const key = toKey(date);
        const weekday = date.getDay();
        const isDelivery = DELIVERY_DATES.has(key);
        const isSeasonKickoff = key === SEASON_KICKOFF_DATE;
        const isPreseason = key < SEASON_KICKOFF_DATE;
        const isSkip = isNoDeliveryDate(key, weekday);
        const isClosed = weekday === 0 || weekday === 6 || SCHEDULED_CLOSURE_DATES.has(key);
        const classes = ['pm-delivery-calendar__day'];
        const descriptions = [];

        if (isDelivery) {
          classes.push('pm-delivery-calendar__day--delivery');
          if (isSeasonKickoff) {
            classes.push('pm-delivery-calendar__day--kickoff');
            descriptions.push('first Purefect Meals pickup of the season');
          } else {
            descriptions.push('Purefect Meals pickup day');
          }
        } else if (isSkip) {
          classes.push('pm-delivery-calendar__day--skip');
          descriptions.push('no Purefect Meals pickup this week');
        } else if (isClosed) {
          classes.push('pm-delivery-calendar__day--closed');
          descriptions.push('scheduled service closure');
        } else if (isPreseason) {
          classes.push('pm-delivery-calendar__day--preseason');
          descriptions.push('before the first scheduled pickup');
        } else {
          descriptions.push('regular service weekday');
        }

        if (key === todayKey) {
          classes.push('pm-delivery-calendar__day--today');
          descriptions.push('today');
        }

        const fullDate = readableDate(key, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
        cells.push(`<td class="${classes.join(' ')}" aria-label="${fullDate}, ${descriptions.join(', ')}"><time datetime="${key}" aria-hidden="true">${day}</time></td>`);
      }

      while (cells.length % 7 !== 0) {
        cells.push('<td class="pm-delivery-calendar__day pm-delivery-calendar__day--empty"><span class="visually-hidden">Not part of this month</span></td>');
      }

      const rows = [];
      for (let index = 0; index < cells.length; index += 7) {
        rows.push(`<tr>${cells.slice(index, index + 7).join('')}</tr>`);
      }
      this.grid.innerHTML = rows.join('');
    }

    renderSummary() {
      const { year, month } = this.view;
      const monthPrefix = `${year}-${pad(month + 1)}-`;
      const deliveries = DELIVERY_DATE_KEYS.filter((key) => key.startsWith(monthPrefix));
      const skips = NO_DELIVERY_WEEKS.filter((range) => rangesOverlapMonth(range, year, month));
      const kickoffDate = fromKey(SEASON_KICKOFF_DATE);
      const isBeforeKickoffMonth = compareMonth(
        { year, month },
        { year: kickoffDate.getFullYear(), month: kickoffDate.getMonth() }
      ) < 0;

      if (isBeforeKickoffMonth) {
        this.deliverySummaryLabel.textContent = 'Season begins';
        this.deliveryList.innerHTML = `<span class="pm-delivery-calendar__delivery-chip pm-delivery-calendar__delivery-chip--kickoff">${readableDate(SEASON_KICKOFF_DATE)} · First delivery</span>`;
      } else {
        this.deliverySummaryLabel.textContent = 'Expected pickups';
        this.deliveryList.innerHTML = deliveries.length
          ? deliveries.map((key) => {
            const isSeasonKickoff = key === SEASON_KICKOFF_DATE;
            const chipClass = isSeasonKickoff
              ? 'pm-delivery-calendar__delivery-chip pm-delivery-calendar__delivery-chip--kickoff'
              : 'pm-delivery-calendar__delivery-chip';
            const chipLabel = isSeasonKickoff
              ? `${readableDate(key)} · First pickup`
              : readableDate(key);
            return `<span class="${chipClass}">${chipLabel}</span>`;
          }).join('')
          : '<p class="pm-delivery-calendar__none">No scheduled pickups this month.</p>';
      }

      this.skipList.innerHTML = skips.length
        ? skips.map((range) => {
            const start = readableDate(range.start, { month: 'short', day: 'numeric' });
            const end = readableDate(range.end, { month: 'short', day: 'numeric' });
            return `<div class="pm-delivery-calendar__skip-item"><strong>${start} to ${end}</strong><span>${range.reason}</span></div>`;
          }).join('')
        : '<p class="pm-delivery-calendar__none">No skipped pickup weeks this month.</p>';
    }
  }

  class PurefectDeliveryStatus {
    constructor(root) {
      this.root = root;
      this.lead = root.querySelector('[data-pm-delivery-status-lead]');
      this.nextGroup = root.querySelector('[data-pm-delivery-status-next]');
      this.nextLabel = root.querySelector('[data-pm-delivery-status-next-label]');
      this.date = root.querySelector('[data-pm-delivery-status-date]');
      this.note = root.querySelector('[data-pm-delivery-status-note]');
      this.openButton = root.querySelector('[data-pm-delivery-status-open]');
      this.defaultNote = this.note.textContent;
      this.nextDeliveryKey = null;
      this.renderedDeliveryKey = undefined;
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.bindEvents();
      this.update();
      this.refreshInterval = window.setInterval(() => this.update(), 60000);
    }

    bindEvents() {
      this.openButton.addEventListener('click', () => {
        document.dispatchEvent(new CustomEvent(OPEN_CALENDAR_EVENT, {
          detail: {
            date: this.nextDeliveryKey,
            trigger: this.openButton
          }
        }));
      });
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    handleVisibilityChange() {
      if (document.visibilityState === 'visible') this.update();
    }

    update() {
      const nextKey = nextDeliveryKeyFor();
      this.nextDeliveryKey = nextKey;
      if (nextKey === this.renderedDeliveryKey) return;
      this.renderedDeliveryKey = nextKey;

      if (!nextKey) {
        this.root.classList.add('pm-delivery-status--pending');
        this.lead.textContent = this.root.dataset.pmStatusScheduleUpdate;
        this.nextGroup.hidden = true;
        this.date.removeAttribute('datetime');
        this.note.textContent = this.root.dataset.pmStatusSchedulePending;
        return;
      }

      this.root.classList.remove('pm-delivery-status--pending');
      this.lead.textContent = this.root.dataset.pmStatusOrderToday;
      this.nextGroup.hidden = false;
      this.nextLabel.textContent = this.root.dataset.pmStatusNextDelivery;
      this.date.dateTime = nextKey;
      this.date.textContent = readableDate(nextKey, { weekday: 'long', month: 'long', day: 'numeric' });
      this.note.textContent = this.defaultNote;
    }
  }

  window.PurefectDeliverySchedule = Object.freeze({
    deliveryDateKeys: DELIVERY_DATE_KEYS.slice(),
    nextDeliveryKeyFor: () => nextDeliveryKeyFor(),
    readableDate
  });
  document.dispatchEvent(new CustomEvent('pm:delivery-schedule:ready'));

  const initialize = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.pmCalendarInitialized === 'true') return;
      root.dataset.pmCalendarInitialized = 'true';
      new PurefectDeliveryCalendar(root);
    });
    document.querySelectorAll(STATUS_SELECTOR).forEach((root) => {
      if (root.dataset.pmDeliveryStatusInitialized === 'true') return;
      root.dataset.pmDeliveryStatusInitialized = 'true';
      new PurefectDeliveryStatus(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', initialize);
})();
